import React, { useState } from 'react';
import styles from './DatePicker.module.scss';

const DatePicker = ({ value, onChange, type, placeholder }) => {
    const [showCalendar, setShowCalendar] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(new Date());

    const nepaliMonths = [
        'बैशाख', 'जेठ', 'असार', 'श्रावण', 'भदौ', 'असोज',
        'कार्तिक', 'मंसिर', 'पुष', 'माघ', 'फाल्गुन', 'चैत'
    ];

    const englishMonths = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const nepaliWeekdays = ['आइत', 'सोम', 'मंगल', 'बुध', 'बिहि', 'शुक्र', 'शनि'];
    const englishWeekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    // Nepali digits mapping
    const englishToNepaliDigits = {
        '0': '०', '1': '१', '2': '२', '3': '३', '4': '४',
        '5': '५', '6': '६', '7': '७', '8': '८', '9': '९'
    };

    // Convert English digits to Nepali digits
    const convertToNepaliDigits = (number) => {
        return String(number).split('').map(digit => englishToNepaliDigits[digit] || digit).join('');
    };

    // Generate calendar days
    const generateCalendarDays = (year, month) => {
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDay = firstDay.getDay();
        
        const days = [];
        
        // Add empty cells for days before the first day of the month
        for (let i = 0; i < startingDay; i++) {
            days.push(null);
        }
        
        // Add days of the month
        for (let i = 1; i <= daysInMonth; i++) {
            days.push(i);
        }
        
        return days;
    };

    const handleDateSelect = (day) => {
        if (!day) return;
        
        if (type === 'nepali') {
            // For Nepali calendar, convert to Nepali year
            const englishYear = currentMonth.getFullYear();
            const nepaliYear = englishYear + 57; // Convert to Nepali year
            const month = String(currentMonth.getMonth() + 1).padStart(2, '0');
            const dayStr = String(day).padStart(2, '0');
            // Format as yyyy-mm-dd for Nepali date with Nepali digits
            const nepaliFormatted = `${convertToNepaliDigits(nepaliYear)}-${convertToNepaliDigits(month)}-${convertToNepaliDigits(dayStr)}`;
            
            onChange(nepaliFormatted);
        } else {
            // For English calendar, use standard format
            const selectedDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
            const formattedDate = selectedDate.toISOString().split('T')[0];
            
            onChange(formattedDate);
        }
        
        setShowCalendar(false);
    };

    // Update current month when value changes externally
    React.useEffect(() => {
        if (value) {
            const parts = value.split('-');
            if (parts.length === 3) {
                let year, month;
                
                if (type === 'nepali') {
                    // Parse yyyy-mm-dd format for Nepali dates
                    // Convert Nepali digits to English for parsing
                    const nepaliToEnglishDigits = {
                        '०': '0', '१': '1', '२': '2', '३': '3', '४': '4',
                        '५': '5', '६': '6', '७': '7', '८': '8', '९': '9'
                    };
                    
                    let yearStr = parts[0];
                    let monthStr = parts[1];
                    let dayStr = parts[2];
                    
                    // Convert Nepali digits to English
                    Object.keys(nepaliToEnglishDigits).forEach(nepaliDigit => {
                        yearStr = yearStr.replace(new RegExp(nepaliDigit, 'g'), nepaliToEnglishDigits[nepaliDigit]);
                        monthStr = monthStr.replace(new RegExp(nepaliDigit, 'g'), nepaliToEnglishDigits[nepaliDigit]);
                        dayStr = dayStr.replace(new RegExp(nepaliDigit, 'g'), nepaliToEnglishDigits[nepaliDigit]);
                    });
                    
                    month = parseInt(monthStr) - 1; // Month is 0-indexed
                    year = parseInt(yearStr);
                    // Convert Nepali year to English year for calendar display
                    const englishYear = year - 57;
                    setCurrentMonth(new Date(englishYear, month, 1));
                } else {
                    // Parse yyyy-mm-dd format for English dates
                    year = parseInt(parts[0]);
                    month = parseInt(parts[1]) - 1; // Month is 0-indexed
                    setCurrentMonth(new Date(year, month, 1));
                }
            }
        }
    }, [value, type]);

    // Get the display year based on calendar type
    const getDisplayYear = () => {
        const englishYear = currentMonth.getFullYear();
        return type === 'nepali' ? englishYear + 57 : englishYear;
    };

    // Get the selected day for highlighting
    const getSelectedDay = () => {
        if (!value) return null;
        const parts = value.split('-');
        if (parts.length === 3) {
            if (type === 'nepali') {
                // For Nepali dates in yyyy-mm-dd format, day is third part
                // Convert Nepali digits to English for comparison
                const nepaliToEnglishDigits = {
                    '०': '0', '१': '1', '२': '2', '३': '3', '४': '4',
                    '५': '5', '६': '6', '७': '7', '८': '8', '९': '9'
                };
                let dayStr = parts[2];
                Object.keys(nepaliToEnglishDigits).forEach(nepaliDigit => {
                    dayStr = dayStr.replace(new RegExp(nepaliDigit, 'g'), nepaliToEnglishDigits[nepaliDigit]);
                });
                return parseInt(dayStr);
            } else {
                // For English dates in yyyy-mm-dd format, day is third part
                return parseInt(parts[2]);
            }
        }
        return null;
    };

    const calendarDays = generateCalendarDays(currentMonth.getFullYear(), currentMonth.getMonth());
    const months = type === 'nepali' ? nepaliMonths : englishMonths;
    const weekdays = type === 'nepali' ? nepaliWeekdays : englishWeekdays;
    const selectedDay = getSelectedDay();

        return (
        <div className={styles['date-picker-container']}>
            <input
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onClick={() => setShowCalendar(!showCalendar)}
                placeholder={placeholder}
                readOnly
                className={styles['date-input']}
            />
            
            {showCalendar && (
                <div className={styles['calendar-popup']}>
                    {type === 'nepali' ? (
                        // Nepali calendar with year-month-day selection
                        <div className={styles['nepali-calendar']}>
                            <div className={styles['calendar-header']}>
                                <h3>Nepali Date Selection</h3>
                            </div>
                            
                            <div className={styles['date-selectors']}>
                                {/* Year Selection */}
                                <div className={styles['selector-group']}>
                                    <label>Year (वर्ष)</label>
                                    <select 
                                        value={getDisplayYear()}
                                        onChange={(e) => {
                                            const selectedYear = parseInt(e.target.value);
                                            const englishYear = selectedYear - 57;
                                            setCurrentMonth(new Date(englishYear, currentMonth.getMonth(), 1));
                                        }}
                                        className={styles['year-select']}
                                    >
                                        {Array.from({length: 20}, (_, i) => {
                                            const year = 2070 + i;
                                            return (
                                                <option key={year} value={year}>
                                                    {convertToNepaliDigits(year)}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </div>
                                
                                {/* Month Selection */}
                                <div className={styles['selector-group']}>
                                    <label>Month (महिना)</label>
                                    <select 
                                        value={currentMonth.getMonth()}
                                        onChange={(e) => {
                                            const selectedMonth = parseInt(e.target.value);
                                            setCurrentMonth(new Date(currentMonth.getFullYear(), selectedMonth, 1));
                                        }}
                                        className={styles['month-select']}
                                    >
                                        {nepaliMonths.map((month, index) => (
                                            <option key={index} value={index}>
                                                {month}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            
                            {/* Calendar Grid */}
                            <div className={styles['calendar-grid']}>
                                <div className={styles['weekdays']}>
                                    {nepaliWeekdays.map(day => (
                                        <div key={day} className={styles['weekday']}>{day}</div>
                                    ))}
                                </div>
                                <div className={styles['days']}>
                                    {calendarDays.map((day, index) => (
                                        <div
                                            key={index}
                                            className={`${styles['day']} ${!day ? styles['empty'] : ''} ${day === selectedDay ? styles['selected'] : ''}`}
                                            onClick={() => handleDateSelect(day)}
                                            data-nepali="true"
                                        >
                                            {day ? convertToNepaliDigits(day) : ''}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ) : (
                        // English calendar (original)
                        <>
                            <div className={styles['calendar-header']}>
                                <button 
                                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                                    className={styles['nav-button']}
                                >
                                    &lt;
                                </button>
                                <span className={styles['month-year']}>
                                    {months[currentMonth.getMonth()]} {getDisplayYear()}
                                </span>
                                <button 
                                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                                    className={styles['nav-button']}
                                >
                                    &gt;
                                </button>
                            </div>
                            
                            <div className={styles['calendar-grid']}>
                                <div className={styles['weekdays']}>
                                    {weekdays.map(day => (
                                        <div key={day} className={styles['weekday']}>{day}</div>
                                    ))}
                                </div>
                                <div className={styles['days']}>
                                    {calendarDays.map((day, index) => (
                                        <div
                                            key={index}
                                            className={`${styles['day']} ${!day ? styles['empty'] : ''} ${day === selectedDay ? styles['selected'] : ''}`}
                                            onClick={() => handleDateSelect(day)}
                                            data-nepali="false"
                                        >
                                            {day}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default DatePicker;
